import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Header } from './Header'
import { Footer } from './Footer'
import { NoiseBackground } from '@/components/ui/noise-background'
import { CookieConsent } from '@/components/ui/cookie-consent'
import { SunsetBanner } from '@/components/ui/sunset-banner'
import * as gtag from '@/lib/gtag'

gsap.registerPlugin(ScrollTrigger)

// Configure ScrollTrigger defaults for mobile stability
ScrollTrigger.defaults({
  invalidateOnRefresh: true,
})

// Configure for mobile address bar changes
ScrollTrigger.config({
  ignoreMobileResize: true,
})


function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    // Ensure scroll reset works across all browsers
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0 // For Safari
  }, [pathname])

  return null
}

// Track page views on route change
function usePageViewTracking() {
  const location = useLocation()

  useEffect(() => {
    gtag.pageview(location.pathname + location.search)
  }, [location])
}

// Configure ScrollTrigger for mobile touch support
function useScrollTriggerConfig() {
  useEffect(() => {
    // Throttled ScrollTrigger.update for touch events
    let ticking = false
    const throttledUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          ScrollTrigger.update()
          ticking = false
        })
        ticking = true
      }
    }

    // Touch events need explicit update calls on some mobile browsers
    window.addEventListener('touchmove', throttledUpdate, { passive: true })
    window.addEventListener('touchend', throttledUpdate, { passive: true })

    // Refresh on orientation change with delay for iOS Safari
    const handleOrientationChange = () => {
      setTimeout(() => ScrollTrigger.refresh(), 200)
    }
    window.addEventListener('orientationchange', handleOrientationChange)

    // Initial refresh after mount
    ScrollTrigger.refresh()

    return () => {
      window.removeEventListener('touchmove', throttledUpdate)
      window.removeEventListener('touchend', throttledUpdate)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])
}

export function Layout() {
  useScrollTriggerConfig()
  usePageViewTracking()

  return (
    <div className="min-h-screen flex flex-col relative">
      <ScrollToTop />

      {/* Noise Background - fixed to viewport */}
      <NoiseBackground
        baseFrequency={0.54}
        numOctaves={4}
        opacity={0.22}
        animate={false}
        position="fixed"
      />

      {/* Shadow Visual - Global */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-80 flex items-end justify-center">
        {/* Desktop shadow */}
        <div className="hidden md:block relative w-full aspect-[1440/658] scale-[1.3] opacity-90 mix-blend-darken">
          <img
            src="/shadow.png"
            alt=""
            className="absolute inset-0 w-full h-full -bottom-8 object-cover origin-bottom"
          />
        </div>
        {/* Mobile shadow */}
        <div className="md:hidden relative w-full aspect-[1/1.2] scale-[1.5] mix-blend-darken">
          <img
            src="/shadow-mobile.png"
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Main Content Wrapper */}
      <div className="flex-1 relative">
        {/* Sunset Banner */}
        <SunsetBanner />

        {/* Header (absolute positioned) */}
        <Header />

        {/* Main Content */}
        <main className="flex-1">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <Footer />

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  )
}
